import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, Phone, ShoppingBag, X } from "lucide-react";
import logoBottega from "@/assets/logo-bottega.webp";
import { useBookingCount } from "@/store/booking";
import { cn } from "@/lib/utils";
import { useRecipesHidden, useProductsHidden } from "@/hooks/useSiteSettings";

const NAV_ALL = [
  { to: "/chi-siamo", label: "Chi siamo" },
  { to: "/prodotti", label: "Prodotti" },
  { to: "/ricette", label: "Ricette" },
  { to: "/abatina", label: "Abatina" },
  { to: "/contatti", label: "Contatti" },
] as const;

export function Navbar(_props: { transparent?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const count = useBookingCount();
  const recipesHidden = useRecipesHidden();
  const productsHidden = useProductsHidden();
  const NAV = NAV_ALL.filter(
    (n) => !(recipesHidden && n.to === "/ricette") && !(productsHidden && n.to === "/prodotti"),
  );
  const NAV_MOBILE = [{ to: "/", label: "Home" } as const, ...NAV];

  useEffect(() => {
    setMounted(true);
  }, []);

  // Lock body scroll when drawer open; always restore on close/unmount
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [open]);

  // Swipe-to-close (left → right) on mobile
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchDeltaX = useRef(0);
  const touchDeltaY = useRef(0);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchDeltaX.current = 0;
    touchDeltaY.current = 0;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current == null || touchStartY.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
    touchDeltaY.current = e.touches[0].clientY - touchStartY.current;
  };
  const onTouchEnd = () => {
    // Only close if the swipe is clearly horizontal — do not fight vertical scroll
    if (touchDeltaX.current > 80 && Math.abs(touchDeltaX.current) > Math.abs(touchDeltaY.current)) {
      setOpen(false);
    }
    touchStartX.current = null;
    touchStartY.current = null;
    touchDeltaX.current = 0;
    touchDeltaY.current = 0;
  };

  // Extra safety: ensure body scroll is always restored on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <header
      className="sticky top-0 z-50 bg-brand-cream/95 backdrop-blur shadow-[0_1px_0_rgba(42,37,32,0.06)]"
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="h-16 sm:h-20 xl:h-24 flex items-center gap-3 sm:gap-4 xl:gap-6">
          <Link
            to="/"
            aria-label="La Bottega delle Carni da Marco"
            className="flex items-center shrink-0 min-w-0"
          >
            <img
              src={logoBottega}
              alt="La Bottega delle Carni da Marco"
              width={1600}
              height={478}
              className="h-10 sm:h-14 xl:h-16 w-auto max-w-[10rem] sm:max-w-[14rem] xl:max-w-[18rem] object-contain"
            />
          </Link>

          <nav className="hidden xl:flex items-center justify-center gap-5 2xl:gap-7 flex-1 min-w-0">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="text-[13px] uppercase tracking-[0.18em] transition-colors text-brand-charcoal hover:text-brand-red whitespace-nowrap"
                activeProps={{
                  className: "text-[13px] uppercase tracking-[0.18em] font-medium text-brand-red whitespace-nowrap",
                }}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0 ml-auto">
            <a
              href="tel:+393407487310"
              className="hidden xl:inline-flex items-center gap-2 text-sm whitespace-nowrap text-brand-charcoal"
            >
              <Phone size={16} /> 340 748 7310
            </a>
            <Link
              to="/richieste"
              className="relative inline-flex items-center justify-center gap-2 h-10 sm:h-11 px-3 sm:px-4 rounded-md transition-colors whitespace-nowrap bg-brand-red text-white hover:bg-brand-red-hover"
            >
              <ShoppingBag size={16} />
              <span className="hidden sm:inline text-[12px] uppercase tracking-[0.16em] whitespace-nowrap">
                Richieste
              </span>
              {count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-brand-gold text-brand-charcoal text-[10px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            <button
              type="button"
              className="xl:hidden inline-flex items-center justify-center w-11 h-11 text-brand-charcoal"
              aria-label="Apri menu"
              onClick={() => setOpen(true)}
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </div>

      {mounted && createPortal(<div
        className={cn(
          "xl:hidden fixed inset-0 z-[60] transition-opacity duration-300",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      >
        <aside
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className={cn(
            "absolute inset-0 w-full h-full bg-brand-cream shadow-xl flex flex-col transition-transform duration-300 overflow-y-auto",
            open ? "translate-x-0" : "translate-x-full"
          )}
        >
          <div className="flex justify-between items-center p-5 sm:p-6 border-b border-brand-charcoal/10">
            <img src={logoBottega} alt="La Bottega delle Carni da Marco" width={1600} height={478} className="h-16 sm:h-20 w-auto max-w-[14rem] sm:max-w-[16rem] object-contain" />
            <button type="button" onClick={() => setOpen(false)} aria-label="Chiudi menu" className="text-brand-charcoal shrink-0 w-11 h-11 inline-flex items-center justify-center">
              <X size={28} />
            </button>
          </div>
          <nav className="flex-1 flex items-center justify-center px-6 py-8">
            <div className="w-full max-w-[26rem] mx-auto flex flex-col items-stretch gap-1">
              {NAV_MOBILE.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="min-h-[52px] flex items-center justify-center text-center text-xl sm:text-2xl font-display text-brand-charcoal hover:text-brand-red transition-colors"
                >
                  {n.label}
                </Link>
              ))}
              <div className="mt-6 flex flex-col gap-3">
                <Link
                  to="/richieste"
                  onClick={() => setOpen(false)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-red text-white py-3 px-6 rounded-md hover:bg-brand-red-hover transition-colors whitespace-nowrap min-h-[48px]"
                >
                  <ShoppingBag size={16} /> Richieste {count > 0 && `(${count})`}
                </Link>
                <a
                  href="tel:+393407487310"
                  className="w-full inline-flex items-center justify-center gap-2 border border-brand-charcoal/30 py-3 px-6 rounded-md text-brand-charcoal hover:border-brand-red hover:text-brand-red transition-colors whitespace-nowrap min-h-[48px]"
                >
                  <Phone size={16} /> Chiama ora
                </a>
              </div>
            </div>
          </nav>
        </aside>
      </div>, document.body)}
    </header>
  );
}
